import { NormalizedSupportPolicy } from 'src/graphql/entities/support-policy/support-policy.utils';
import { POLICY_CATEGORIES } from './policy-constants';
import { getSeverityForType } from './policy-severity';

export const POLICY_CATEGORY_ORDER = POLICY_CATEGORIES;

const FALLBACK_CATEGORY = 'General';

/**
 * A category the UI does not know — a legacy row, or an enum value added ahead of
 * the frontend — reads as General so it is never silently dropped. Grouping,
 * filtering and counting must all resolve through this, or a policy shows under
 * General yet vanishes the moment someone filters to General.
 */
export const resolvePolicyCategory = (category?: string | null): string =>
  POLICY_CATEGORY_ORDER.includes(category as (typeof POLICY_CATEGORY_ORDER)[number])
    ? category!
    : FALLBACK_CATEGORY;

export interface PolicyCategoryGroup {
  category: string;
  policies: NormalizedSupportPolicy[];
  restrictionCount: number;
}

/**
 * Partitions an already-sorted list into call-flow categories. Nothing re-sorts
 * inside a group: the caller's order (the query's pinned block, escalation by
 * urgency) survives verbatim. Empty categories are dropped.
 */
export const groupPoliciesByCategory = (
  sortedPolicies: NormalizedSupportPolicy[],
): PolicyCategoryGroup[] => {
  const buckets = new Map<string, NormalizedSupportPolicy[]>();

  sortedPolicies.forEach((policy) => {
    const category = resolvePolicyCategory(policy.category);
    const bucket = buckets.get(category);

    if (bucket) {
      bucket.push(policy);
    } else {
      buckets.set(category, [policy]);
    }
  });

  return POLICY_CATEGORY_ORDER.reduce<PolicyCategoryGroup[]>((groups, category) => {
    const policies = buckets.get(category);

    if (policies?.length) {
      groups.push({
        category,
        policies,
        restrictionCount: policies.filter(
          (policy) => getSeverityForType(policy.type) === 'restriction',
        ).length,
      });
    }

    return groups;
  }, []);
};
